/**
 * generateM365ConfigDocs.ts - AutoDoc configuration des services M365.
 *   - downloadExoConfigDocx    : Configuration messagerie Exchange Online
 *   - downloadSpoConfigDocx    : Configuration SharePoint / OneDrive
 *   - downloadTeamsConfigDocx  : Configuration des services Teams
 * Donnees lues en direct (Graph / Exchange Admin REST).
 */
import { Document, Packer, Paragraph, Table, TableRow, WidthType, HeadingLevel, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import {
  AUTODOC_STYLES, buildCover, buildIntro, cell, headerCell, safeName,
} from './autodocCommon';
import type { AutodocMeta } from './generateAutodocReports';
import type {
  ExoConfigFacts, SpoConfigFacts, TeamsConfigFacts,
} from '../api/m365ConfigFactsApi';

function para(text: string, size = 20): Paragraph {
  return new Paragraph({ children: [new TextRun({ text, size })] });
}
function h1(text: string): Paragraph {
  return new Paragraph({ text, heading: HeadingLevel.HEADING_1, pageBreakBefore: true });
}
const OK = 'Oui', NO = 'Non';
const yn = (b?: boolean) => (b ? OK : NO);
const dash = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));
const okColor = (b: boolean) => (b ? '1F7A45' : 'B82329');

function kvTable(rows: [string, string][]): Table {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: rows.map(([k, v]) => new TableRow({ children: [headerCell(k, 42), cell(v || '—')] })),
  });
}

// ===========================================================================
//  Exchange Online
// ===========================================================================
export async function downloadExoConfigDocx(params: { meta: AutodocMeta; facts: ExoConfigFacts }): Promise<void> {
  const { meta, facts } = params;
  const s = facts.summary;
  const cover = await buildCover({
    brandLine: 'AutoDoc',
    title:     'Configuration messagerie Exchange Online',
    subtitle:  'Domaines, authentification email (SPF/DKIM/DMARC), anti-spam & anti-phishing, connecteurs',
    orgName:   meta.orgName,
    logo:      meta.logo,
    boxLines:  [['Domaines', String(s?.domains ?? facts.acceptedDomains.length)]],
    footnote:  'Configuration extraite en direct via l’API Exchange Online.',
  });
  const intro = buildIntro({
    purpose: 'Documenter la configuration de la messagerie Exchange Online : domaines acceptés, authentification de messagerie (SPF, DKIM, DMARC), politiques anti-spam et anti-phishing, connecteurs entrants/sortants.',
    audience: 'RSSI, équipe messagerie/M365, auditeur.',
    scope: meta.tenantDomain ? `Tenant ${meta.tenantDomain}.` : 'Tenant Microsoft 365 connecté.',
  });

  const body: (Paragraph | Table)[] = [];

  // Synthèse
  body.push(h1('Synthèse'));
  if (s) {
    body.push(para(`${s.domains} domaine(s) · DKIM activé : ${s.dkimEnabled} · SPF présent : ${s.spfOk} · DMARC présent : ${s.dmarcOk} · ${s.connectors} connecteur(s).`, 18));
  }
  body.push(new Paragraph({ children: [] }));

  // Domaines acceptés
  body.push(h1('Domaines acceptés'));
  if (facts.acceptedDomains.length > 0) {
    body.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [
      new TableRow({ tableHeader: true, children: [headerCell('Domaine', 42), headerCell('Type', 30), headerCell('Par défaut', 28)] }),
      ...facts.acceptedDomains.map((d) => new TableRow({ children: [
        cell(d.domainName || d.name), cell(d.domainType), cell(yn(d.default)),
      ] })),
    ] }));
  } else { body.push(para('Aucun domaine accepté récupéré.')); }
  body.push(new Paragraph({ children: [] }));

  // Authentification email par domaine (SPF / DKIM / DMARC)
  body.push(h1('Authentification de messagerie (SPF / DKIM / DMARC)'));
  const dkimByDomain = new Map(facts.dkim.map((k) => [k.domain?.toLowerCase(), k]));
  const domainsForAuth = facts.dns.length > 0
    ? facts.dns.map((x) => x.domain)
    : facts.acceptedDomains.map((d) => d.domainName);
  if (domainsForAuth.length > 0) {
    body.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [
      new TableRow({ tableHeader: true, children: [
        headerCell('Domaine', 34), headerCell('SPF', 16), headerCell('DKIM', 16), headerCell('DMARC', 34),
      ] }),
      ...domainsForAuth.map((dom) => {
        const dnsRow = facts.dns.find((x) => x.domain === dom);
        const dk = dkimByDomain.get(dom?.toLowerCase());
        const spfFound = Boolean(dnsRow?.spf.found);
        const dkimOn = Boolean(dk?.enabled);
        const dmarcFound = Boolean(dnsRow?.dmarc.found);
        const dmarcTxt = dmarcFound ? `Oui (p=${dnsRow?.dmarc.policy ?? '?'})` : 'Non';
        return new TableRow({ children: [
          cell(dom),
          cell(spfFound ? OK : NO, { bold: true, color: okColor(spfFound) }),
          cell(dkimOn ? OK : NO, { bold: true, color: okColor(dkimOn) }),
          cell(dmarcTxt, { bold: true, color: okColor(dmarcFound) }),
        ] });
      }),
    ] }));
    body.push(para('SPF et DMARC sont lus dans le DNS public du domaine ; DKIM provient d’Exchange Online. Un domaine correctement protégé présente SPF + DKIM + DMARC (idéalement p=quarantine ou p=reject).', 16));
  } else { body.push(para('Aucune donnée d’authentification email.')); }
  body.push(new Paragraph({ children: [] }));

  // Anti-spam
  body.push(h1('Politiques anti-spam'));
  if (facts.antiSpam.length > 0) {
    body.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [
      new TableRow({ tableHeader: true, children: [
        headerCell('Politique', 30), headerCell('Défaut', 12), headerCell('Seuil bulk', 14), headerCell('Action spam', 22), headerCell('Action phish', 22),
      ] }),
      ...facts.antiSpam.map((p) => new TableRow({ children: [
        cell(p.name, { bold: true }), cell(yn(p.default)), cell(dash(p.bulkThreshold)),
        cell(dash(p.spamAction)), cell(dash(p.phishSpamAction)),
      ] })),
    ] }));
  } else { body.push(para('Aucune politique anti-spam récupérée.')); }
  body.push(new Paragraph({ children: [] }));

  // Anti-phishing
  body.push(h1('Politiques anti-phishing'));
  if (facts.antiPhish.length > 0) {
    body.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [
      new TableRow({ tableHeader: true, children: [
        headerCell('Politique', 28), headerCell('Activée', 12), headerCell('Seuil', 12), headerCell('Mailbox Intel.', 16), headerCell('Anti-spoof', 14), headerCell('Action DMARC', 18),
      ] }),
      ...facts.antiPhish.map((p) => new TableRow({ children: [
        cell(p.name, { bold: true }),
        cell(p.enabled ? OK : NO, { bold: true, color: okColor(Boolean(p.enabled)) }),
        cell(dash(p.phishThresholdLevel)), cell(yn(p.mailboxIntelligence)), cell(yn(p.spoofIntelligence)), cell(dash(p.dmarcRejectAction)),
      ] })),
    ] }));
  } else { body.push(para('Aucune politique anti-phishing récupérée.')); }
  body.push(new Paragraph({ children: [] }));

  // Connecteurs
  body.push(h1('Connecteurs'));
  const connRows = (arr: { name: string; enabled: boolean; connectorType: string }[], label: string): (Paragraph | Table)[] => {
    const out: (Paragraph | Table)[] = [para(label, 18)];
    if (arr.length > 0) {
      out.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [
        new TableRow({ tableHeader: true, children: [headerCell('Nom', 50), headerCell('Activé', 20), headerCell('Type', 30)] }),
        ...arr.map((c) => new TableRow({ children: [cell(c.name), cell(yn(c.enabled)), cell(c.connectorType)] })),
      ] }));
    } else { out.push(para('Aucun.', 18)); }
    out.push(new Paragraph({ children: [] }));
    return out;
  };
  body.push(...connRows(facts.connectors.inbound, 'Connecteurs entrants :'));
  body.push(...connRows(facts.connectors.outbound, 'Connecteurs sortants :'));

  const doc = new Document({
    creator: 'NIS365 Auditor', title: `Config Exchange Online - ${meta.orgName}`, styles: AUTODOC_STYLES,
    sections: [{ children: [...cover, ...intro, ...body] }],
  });
  saveAs(await Packer.toBlob(doc), `Config_Exchange_Online_${safeName(meta.orgName)}_${new Date().toISOString().slice(0, 10)}.docx`);
}

// ===========================================================================
//  SharePoint / OneDrive
// ===========================================================================
export async function downloadSpoConfigDocx(params: { meta: AutodocMeta; facts: SpoConfigFacts }): Promise<void> {
  const { meta, facts } = params;
  const cover = await buildCover({
    brandLine: 'AutoDoc',
    title:     'Configuration SharePoint / OneDrive',
    subtitle:  'Partage externe, synchronisation et paramètres du tenant',
    orgName:   meta.orgName,
    logo:      meta.logo,
    boxLines:  [['Partage externe', dash(facts.sharing.sharingCapability)]],
    footnote:  'Configuration extraite en direct via Microsoft Graph.',
  });
  const intro = buildIntro({
    purpose: 'Documenter les paramètres tenant SharePoint et OneDrive : partage externe, restrictions de domaine, liens d’accès, synchronisation client et paramètres généraux.',
    audience: 'RSSI, DPO, équipe collaboration/M365, auditeur.',
    scope: meta.tenantDomain ? `Tenant ${meta.tenantDomain}.` : 'Tenant Microsoft 365 connecté.',
  });

  const sh = facts.sharing;
  const sy = facts.sync;
  const body: (Paragraph | Table)[] = [];

  body.push(h1('Partage externe'));
  body.push(kvTable([
    ['Niveau de partage (SharingCapability)', dash(sh.sharingCapability)],
    ['Restriction par domaine', dash(sh.sharingDomainRestrictionMode)],
    ['Domaines autorisés', (sh.sharingAllowedDomainList ?? []).join(', ') || '—'],
    ['Domaines bloqués', (sh.sharingBlockedDomainList ?? []).join(', ') || '—'],
    ['Re-partage par utilisateurs externes', yn(sh.isResharingByExternalUsersEnabled)],
    ['Type de lien par défaut', dash(sh.defaultSharingLinkType)],
    ['Permission par défaut', dash(sh.defaultLinkPermission)],
    ['Expiration des invités requise', yn(sh.externalUserExpirationRequired)],
    ['Expiration invités (jours)', dash(sh.externalUserExpireInDays)],
    ['Lien anonyme fichier', dash(sh.fileAnonymousLinkType)],
    ['Lien anonyme dossier', dash(sh.folderAnonymousLinkType)],
    ['Expiration lien anonyme (jours)', dash(sh.anonymousLinkExpirationInDays)],
  ]));
  body.push(new Paragraph({ children: [] }));

  body.push(h1('Synchronisation (OneDrive Sync)'));
  body.push(kvTable([
    ['App de sync Mac autorisée', yn(sy.isMacSyncAppEnabled)],
    ['Sync restreinte aux appareils gérés', yn(sy.isUnmanagedSyncAppForTenantRestricted)],
    ['Domaines autorisés (GUID)', (sy.allowedDomainGuidsForSyncApp ?? []).join(', ') || '—'],
    ['Extensions exclues de la sync', (sy.excludedFileExtensionsForSyncApp ?? []).join(', ') || '—'],
  ]));
  body.push(new Paragraph({ children: [] }));

  body.push(h1('Paramètres du tenant'));
  const t = facts.tenant || {};
  const tRows: [string, string][] = Object.keys(t).map((k): [string, string] => [k, dash(t[k])]);
  body.push(tRows.length > 0 ? kvTable(tRows) : para('Aucun paramètre tenant récupéré.'));

  const doc = new Document({
    creator: 'NIS365 Auditor', title: `Config SharePoint OneDrive - ${meta.orgName}`, styles: AUTODOC_STYLES,
    sections: [{ children: [...cover, ...intro, ...body] }],
  });
  saveAs(await Packer.toBlob(doc), `Config_SharePoint_OneDrive_${safeName(meta.orgName)}_${new Date().toISOString().slice(0, 10)}.docx`);
}

// ===========================================================================
//  Teams
// ===========================================================================
export async function downloadTeamsConfigDocx(params: { meta: AutodocMeta; facts: TeamsConfigFacts }): Promise<void> {
  const { meta, facts } = params;
  const inv = facts.inventory;
  const g = facts.guestAccess;
  const cover = await buildCover({
    brandLine: 'AutoDoc',
    title:     'Configuration des services Teams',
    subtitle:  'Inventaire des équipes, accès invités & externe',
    orgName:   meta.orgName,
    logo:      meta.logo,
    boxLines:  [['Équipes', String(inv.total)]],
    footnote:  'Données lues en direct via Microsoft Graph.',
  });
  const intro = buildIntro({
    purpose: 'Documenter les services Microsoft Teams : inventaire des équipes et visibilité, accès invités et collaboration externe. Les politiques tenant (réunions, messagerie, appels, fédération) requièrent un accès Teams PowerShell et sont à compléter manuellement.',
    audience: 'RSSI, équipe collaboration/M365, auditeur.',
    scope: meta.tenantDomain ? `Tenant ${meta.tenantDomain}.` : 'Tenant Microsoft 365 connecté.',
  });

  const body: (Paragraph | Table)[] = [];

  body.push(h1('Inventaire des équipes'));
  body.push(kvTable([
    ['Nombre total d’équipes', String(inv.total)],
    ['Équipes publiques', String(inv.public)],
    ['Équipes privées', String(inv.private)],
  ]));
  body.push(new Paragraph({ children: [] }));
  if (inv.sample.length > 0) {
    body.push(para(`Échantillon (${inv.sample.length} équipe(s)) :`, 18));
    body.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [
      new TableRow({ tableHeader: true, children: [headerCell('Équipe', 70), headerCell('Visibilité', 30)] }),
      ...inv.sample.map((x) => new TableRow({ children: [cell(x.displayName), cell(x.visibility)] })),
    ] }));
    body.push(new Paragraph({ children: [] }));
  }

  body.push(h1('Accès invités & collaboration externe'));
  body.push(kvTable([
    ['Qui peut inviter des invités (allowInvitesFrom)', dash(g.allowInvitesFrom)],
    ['Rôle des invités', dash(g.guestUserRole)],
    ['Ajout d’invités aux groupes/équipes autorisé', g.allowToAddGuests === undefined ? '—' : yn(g.allowToAddGuests)],
    ['Accès des invités aux groupes autorisé', g.allowGuestsToAccessGroups === undefined ? '—' : yn(g.allowGuestsToAccessGroups)],
    ['Création de groupes/équipes autorisée', g.enableGroupCreation === undefined ? '—' : yn(g.enableGroupCreation)],
  ]));
  body.push(new Paragraph({ children: [] }));

  body.push(h1('Politiques Teams — à compléter manuellement'));
  body.push(para('Les éléments suivants ne sont pas exposés par Microsoft Graph en accès applicatif et doivent être renseignés depuis le centre d’administration Teams (ou via le module PowerShell MicrosoftTeams) :', 18));
  for (const sec of facts.manualSections) {
    body.push(new Paragraph({ text: sec, bullet: { level: 0 } }));
  }

  const doc = new Document({
    creator: 'NIS365 Auditor', title: `Config Teams - ${meta.orgName}`, styles: AUTODOC_STYLES,
    sections: [{ children: [...cover, ...intro, ...body] }],
  });
  saveAs(await Packer.toBlob(doc), `Config_Teams_${safeName(meta.orgName)}_${new Date().toISOString().slice(0, 10)}.docx`);
}
